/**
 * EngagementPanels — Phase I helpers of the CMS program-audit process.
 *
 * DisclosedIssuesPanel: pre-audit issue summary (PAIS). Issues the auditee
 * reported and began correcting before the engagement letter are treated
 * differently in classification, so they are recorded up front.
 *
 * UniverseAttemptsPanel: the CMS three-attempt universe rule. Each rejected
 * or corrected universe submission is counted; the second becomes an
 * observation in the report and from the third on, untestable elements are
 * cited as Invalid Data Submission (IDS).
 */
import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL;

export function DisclosedIssuesPanel({ auditId }) {
  const [issues, setIssues] = useState([]);
  const [desc, setDesc] = useState('');
  const [date, setDate] = useState('');
  const [correction, setCorrection] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/disclosed-issues`, { credentials: 'include' }).then(r => r.json());
      if (r.success) setIssues(r.issues || []);
    } catch {}
  }, [auditId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!desc.trim()) return;
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/api/audit/audits/${auditId}/disclosed-issues`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: desc.trim(), disclosed_date: date || null, correction_status: correction.trim() || null }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || 'Could not record the issue.');
      setDesc(''); setDate(''); setCorrection('');
      await load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const remove = async (id) => {
    setBusy(true); setErr('');
    try {
      const d = await fetch(`${API}/api/audit/audits/${auditId}/disclosed-issues/${id}`, { method: 'DELETE', credentials: 'include' }).then(r => r.json());
      if (!d.success) throw new Error(d.message || 'Could not remove the issue.');
      await load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div>
        <div className="text-sm font-semibold text-slate-800">
          Pre-audit disclosed issues <span className="text-slate-400 font-normal">· PAIS{issues.length ? ` · ${issues.length}` : ''}</span>
        </div>
        <div className="text-[11px] text-slate-400">Issues the auditee reported before the engagement letter don't count against them the same way — record them here.</div>
      </div>

      {issues.length > 0 && (
        <div className="space-y-2">
          {issues.map(it => (
            <div key={it.id} className="border border-slate-100 rounded-lg px-3 py-2">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0 text-[12px] text-slate-700">{it.description}</div>
                <button type="button" onClick={() => remove(it.id)} disabled={busy} className="text-slate-300 hover:text-red-600 flex-shrink-0" aria-label="Remove disclosed issue">×</button>
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-slate-400 mt-1">
                {it.disclosed_date && <span>Disclosed {String(it.disclosed_date).slice(0, 10)}</span>}
                {it.correction_status && <span>Correction: {it.correction_status}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-[1fr,auto,auto] gap-2 items-start">
        <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="What the auditee disclosed" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
        <input type="date" value={date} onChange={e => setDate(e.target.value)} aria-label="Date disclosed" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
        <button type="button" onClick={add} disabled={busy || !desc.trim()} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">Add</button>
        <input value={correction} onChange={e => setCorrection(e.target.value)} placeholder="Correction status (e.g., corrected, in progress)" className="sm:col-span-3 text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
      </div>
      {err && <div className="text-xs text-red-600">{err}</div>}
    </div>
  );
}

export function UniverseAttemptsPanel({ audit, auditId, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [guidance, setGuidance] = useState('');
  const [err, setErr] = useState('');
  const attempts = Number(audit?.universe_attempts || 0);

  const record = async () => {
    setBusy(true); setErr('');
    try {
      const d = await fetch(`${API}/api/audit/audits/${auditId}/universe-attempt`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}',
      }).then(r => r.json());
      if (!d.success) throw new Error(d.message || 'Could not record the attempt.');
      setGuidance(d.guidance || '');
      if (onChanged) await onChanged();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-800">
            Universe submissions <span className="text-slate-400 font-normal">· {attempts} attempt{attempts === 1 ? '' : 's'} recorded</span>
          </div>
          <div className="text-[11px] text-slate-400">CMS allows three tries: a second submission is documented as an observation in the report, and after a third failed attempt anything untestable is cited as an Invalid Data Submission.</div>
        </div>
        <button type="button" onClick={record} disabled={busy} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
          {busy ? 'Recording…' : 'Record a universe attempt'}
        </button>
      </div>
      {guidance && <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">{guidance}</div>}
      {err && <div className="text-xs text-red-600">{err}</div>}
    </div>
  );
}
